import React, { useState } from 'react';
import { 
  Inbox, 
  Calendar, 
  CheckCircle, 
  Star, 
  ChevronDown,
  Plus,
  LayoutGrid,
  Crown,
  X
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useI18n } from '../i18n';
import { ProfileSettings } from './ProfileSettings';
import { HelpGuide } from './HelpGuide';

import { WorkspaceIcon, WorkspaceIconPicker } from './WorkspaceIcons';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

export function Sidebar({ currentView, onViewChange, mobileOpen, onMobileClose }) {
  const { user } = useAuth();
  const { workspaces, currentWorkspace, switchWorkspace, createWorkspace, tasks } = useWorkspace();
  const { t } = useI18n();
  const [showNewWs, setShowNewWs] = useState(false);
  const [newWsName, setNewWsName] = useState('');
  const [newWsIcon, setNewWsIcon] = useState('bolt');
  const [creating, setCreating] = useState(false);

  const navItems = [
    { id: 'all', label: t('all'), icon: LayoutGrid },
    { id: 'inbox', label: t('inbox'), icon: Inbox },
    { id: 'today', label: t('today'), icon: Calendar },
    { id: 'starred', label: t('starred'), icon: Star },
    { id: 'done', label: t('done'), icon: CheckCircle },
  ];

  const totalTasks = tasks.length;
  const inProgressCount = tasks.filter(t => t.status === 'in_progress').length;
  const doneTodayCount = tasks.filter(t => {
    if (t.status !== 'done') return false;
    const today = new Date().toDateString();
    const updated = new Date(t.updated_at || t.created_at).toDateString();
    return today === updated;
  }).length;

  const handleNavClick = (id) => {
    onViewChange(id);
    if (onMobileClose) onMobileClose();
  };

  const handleCreateWorkspace = async () => {
    if (!newWsName.trim()) return;
    setCreating(true);
    try {
      const ws = await createWorkspace(newWsName.trim(), 'personal', [], newWsIcon);
      switchWorkspace(ws);
      setShowNewWs(false);
      setNewWsName('');
      setNewWsIcon('bolt');
      toast.success('Workspace skapat!');
    } catch (e) {
      const msg = e.response?.data?.detail || 'Kunde inte skapa workspace';
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onMobileClose} />
      )}
      
      {/* Sidebar - hidden on mobile unless mobileOpen */}
      <aside
        className={`${mobileOpen ? 'fixed inset-y-0 left-0 z-50' : 'hidden lg:flex'} w-64 bg-[#0C0C0E] border-r border-white/5 flex-col h-screen flex-shrink-0`}
        style={mobileOpen ? { display: 'flex' } : undefined}
      >
        {/* Mobile close */}
        <div className="lg:hidden flex justify-end p-2">
          <button onClick={onMobileClose} className="p-2 text-zinc-500 hover:text-zinc-300">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Workspace Switcher */}
        <div className="p-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors cursor-pointer">
                <WorkspaceIcon iconId={currentWorkspace?.icon || 'bolt'} size={32} />
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium text-zinc-100 truncate">
                    {currentWorkspace?.name || 'Workspace'}
                  </p>
                  <p className="text-xs text-zinc-500">{currentWorkspace?.type === 'personal' ? t('personal') : t('shared')}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-zinc-500 flex-shrink-0" />
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56 bg-[#141416] border-white/10" align="start">
              {workspaces.map((ws) => (
                <DropdownMenuItem
                  key={ws.id}
                  onClick={() => { switchWorkspace(ws); if (onMobileClose) onMobileClose(); }}
                  className="cursor-pointer focus:bg-white/5"
                  data-testid={`workspace-option-${ws.id}`}
                >
                  <div className="mr-2"><WorkspaceIcon iconId={ws.icon || 'bolt'} size={24} /></div>
                  <span className="text-zinc-100">{ws.name}</span>
                </DropdownMenuItem>
              ))}
              <DropdownMenuItem
                onClick={() => setShowNewWs(true)}
                className="cursor-pointer focus:bg-white/5 border-t border-white/5 mt-1"
                data-testid="new-workspace-btn"
              >
                <Plus className="w-4 h-4 mr-2 text-zinc-500" />
                <span className="text-zinc-400">Nytt workspace</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 space-y-1">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleNavClick(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-sm ${
                currentView === item.id
                  ? 'bg-white/[0.07] text-zinc-100'
                  : 'text-zinc-500 hover:bg-white/[0.03] hover:text-zinc-300'
              }`}
              data-testid={`nav-${item.id}`}
            >
              <item.icon className="w-4 h-4" strokeWidth={1.5} />
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        {/* Stats */}
        <div className="px-6 py-4 border-t border-white/5">
          <p className="text-[10px] uppercase tracking-wider text-zinc-600 mb-3">{t('quickStats')}</p>
          <StatItem label={t('total')} value={totalTasks} />
          <StatItem label={t('inProgress')} value={inProgressCount} />
          <StatItem label={t('doneToday')} value={doneTodayCount} />
        </div>

        {/* Bottom Section */}
        <div className="p-4 border-t border-white/5 space-y-2">
          {user?.plan !== 'pro' && (
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('open-upgrade'))}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/20 hover:from-yellow-500/15 hover:to-orange-500/15 transition-all"
              data-testid="sidebar-upgrade-btn"
            >
              <Crown className="w-4 h-4 text-yellow-400" />
              <span className="text-xs text-yellow-400 font-medium">{t('upgradePro')}</span>
            </button>
          )}

          <HelpGuide />

          <ProfileSettings />
        </div>
      </aside>

      {/* New Workspace Dialog */}
      <Dialog open={showNewWs} onOpenChange={setShowNewWs}>
        <DialogContent className="bg-[#141416] border-white/10 text-zinc-50 max-w-sm" data-testid="new-workspace-modal">
          <DialogHeader>
            <DialogTitle className="text-base font-medium font-['Outfit']">Nytt workspace</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <Input
              value={newWsName}
              onChange={(e) => setNewWsName(e.target.value)}
              placeholder="t.ex. Kundprojekt, Sidoverksamhet"
              className="bg-[#09090B] border-white/10 text-zinc-100 focus:border-zinc-500 h-9 text-sm"
              data-testid="new-workspace-name-input"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleCreateWorkspace()}
            />
            <div>
              <p className="text-xs text-zinc-500 mb-2">Välj ikon</p>
              <WorkspaceIconPicker selected={newWsIcon} onSelect={setNewWsIcon} />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => { setShowNewWs(false); setNewWsName(''); setNewWsIcon('bolt'); }}
                className="flex-1 border-white/10 text-zinc-400 hover:bg-white/5 h-9 text-sm"
              >
                Avbryt
              </Button>
              <Button
                onClick={handleCreateWorkspace}
                disabled={creating || !newWsName.trim()}
                className="flex-1 bg-zinc-100 text-zinc-900 hover:bg-white h-9 text-sm"
                data-testid="create-workspace-submit"
              >
                {creating ? 'Skapar...' : 'Skapa'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function StatItem({ label, value }) {
  return (
    <div className="flex items-center justify-between mb-1.5">
      <span className="text-xs text-zinc-500">{label}</span>
      <span className="text-sm font-medium text-zinc-100 font-['Outfit']">{value}</span>
    </div>
  );
}
